import { createContext, useRef, RefObject, MutableRefObject } from 'react';
import { EditorView } from '@uiw/react-codemirror';
import { Message, ProgrammingLanguage } from 'src/types';
import { VirtuosoMessageListMethods } from '@virtuoso.dev/message-list';
import { SwiperRef } from 'swiper/react';

export type ThreadMessageListHandlers = {
  onScrollToBottomMessageList?: (delay?: number) => void;
  onScrollToBottomComparedMessageList?: (delay?: number) => void;
};

export type FloatingElementPluginHandlers = {
  onElementOpen: () => void;
  onElementClose: () => void;
  get isOpen(): boolean;
};

export type CodeEditorHandlers = {
  createCodeArtifact: (
    language?: ProgrammingLanguage,
    content?: string,
  ) => void;
  createDocumentArtifact: (content?: string) => void;
};

// TODO(olha): check all variables, delete unused
export type ForwardRefContextType = {
  rootContainerRef: RefObject<HTMLDivElement> | null;
  navButtonsRef: RefObject<HTMLDivElement> | null;
  avatarSettingsButtonRef: RefObject<HTMLButtonElement> | null;
  avatarsSelectorButtonRef: RefObject<HTMLButtonElement> | null;
  avatarsSelectorVideoButtonRef: RefObject<HTMLButtonElement> | null;
  avatarsSelectorRef: RefObject<HTMLDivElement> | null;
  avatarBackgroundSelectorRef: RefObject<HTMLDivElement> | null;
  avatarBackgroundSelectorButtonRef: RefObject<HTMLButtonElement> | null;
  avatarCameraSelectorRef: RefObject<HTMLDivElement> | null;
  avatarCameraSelectorButtonRef: RefObject<HTMLButtonElement> | null;
  avatarFrameRef: RefObject<HTMLDivElement> | null;
  iFrameRef: RefObject<HTMLIFrameElement> | null;
  avatarAnchorRef: RefObject<HTMLDivElement> | null;
  threadMessageListRef: RefObject<HTMLDivElement> | null;
  threadInputBoxRef: RefObject<HTMLElement> | null;
  endOfMessagesRef: RefObject<HTMLDivElement> | null;
  /**
   * @deprecated
   */
  endOfFirstCompareMessagesRef: RefObject<HTMLDivElement> | null;
  /**
   * @deprecated
   */
  endOfSecondCompareMessagesRef: RefObject<HTMLDivElement> | null;
  editorFloatingTooltipRef: RefObject<HTMLDivElement> | null;
  floatingElementPluginRef: RefObject<FloatingElementPluginHandlers> | null;
  codeEditorRef: MutableRefObject<EditorView | null>;
  mainMessageListRef: RefObject<VirtuosoMessageListMethods<Message>>;
  compareMessageListRef: RefObject<VirtuosoMessageListMethods<Message>>;
  imageEditorSwiperRef: RefObject<SwiperRef>;
  scrollableConversationsContainerRef: RefObject<HTMLDivElement> | null;
};

type ForwardRefProviderProps = {
  children: React.ReactNode;
};

/**
 * ForwardRefContext is workaround for pass refs to components without using ForwardRef.
 */
const ForwardRefContext = createContext<ForwardRefContextType>({
  rootContainerRef: null,
  avatarSettingsButtonRef: null,
  navButtonsRef: null,
  avatarsSelectorButtonRef: null,
  avatarsSelectorVideoButtonRef: null,
  avatarsSelectorRef: null,
  avatarBackgroundSelectorRef: null,
  avatarBackgroundSelectorButtonRef: null,
  avatarCameraSelectorRef: null,
  avatarCameraSelectorButtonRef: null,
  avatarFrameRef: null,
  iFrameRef: null,
  avatarAnchorRef: null,
  threadMessageListRef: null,
  threadInputBoxRef: null,
  endOfMessagesRef: null,
  endOfFirstCompareMessagesRef: null,
  endOfSecondCompareMessagesRef: null,
  editorFloatingTooltipRef: null,
  floatingElementPluginRef: null,
  codeEditorRef: { current: null },
  mainMessageListRef: { current: null },
  compareMessageListRef: { current: null },
  imageEditorSwiperRef: { current: null },
  scrollableConversationsContainerRef: null,
});

const ForwardRefContextProvider = ({ children }: ForwardRefProviderProps) => {
  const rootContainerRef = useRef<HTMLDivElement>(null);
  const avatarSettingsButtonRef = useRef<HTMLButtonElement>(null);
  const navButtonsRef = useRef<HTMLDivElement>(null);
  const avatarsSelectorButtonRef = useRef<HTMLButtonElement>(null);
  const avatarsSelectorVideoButtonRef = useRef<HTMLButtonElement>(null);
  const avatarsSelectorRef = useRef<HTMLDivElement>(null);
  const avatarBackgroundSelectorRef = useRef<HTMLDivElement>(null);
  const avatarBackgroundSelectorButtonRef = useRef<HTMLButtonElement>(null);
  const avatarCameraSelectorRef = useRef<HTMLDivElement>(null);
  const avatarCameraSelectorButtonRef = useRef<HTMLButtonElement>(null);
  const avatarFrameRef = useRef<HTMLDivElement>(null);
  const iFrameRef = useRef<HTMLIFrameElement>(null);
  const avatarAnchorRef = useRef<HTMLDivElement>(null);
  const threadMessageListRef = useRef<HTMLDivElement>(null);
  const threadInputBoxRef = useRef<HTMLTextAreaElement>(null);
  const endOfMessagesRef = useRef<HTMLDivElement>(null);
  const endOfFirstCompareMessagesRef = useRef<HTMLDivElement>(null);
  const endOfSecondCompareMessagesRef = useRef<HTMLDivElement>(null);
  const editorFloatingTooltipRef = useRef<HTMLDivElement>(null);
  const floatingElementPluginRef = useRef<FloatingElementPluginHandlers>(null);
  const codeEditorRef = useRef<EditorView | null>(null);
  // TODO(olha): WIP Virtuoso
  const mainMessageListRef = useRef<VirtuosoMessageListMethods<Message>>(null);
  const compareMessageListRef =
    useRef<VirtuosoMessageListMethods<Message>>(null);
  const imageEditorSwiperRef = useRef<SwiperRef | null>(null);
  const scrollableConversationsContainerRef = useRef<HTMLDivElement>(null);

  return (
    <ForwardRefContext.Provider
      value={{
        rootContainerRef,
        avatarSettingsButtonRef,
        navButtonsRef,
        avatarsSelectorButtonRef,
        avatarsSelectorVideoButtonRef,
        avatarsSelectorRef,
        avatarBackgroundSelectorRef,
        avatarBackgroundSelectorButtonRef,
        avatarCameraSelectorRef,
        avatarCameraSelectorButtonRef,
        avatarFrameRef,
        iFrameRef,
        avatarAnchorRef,
        threadMessageListRef,
        threadInputBoxRef,
        endOfMessagesRef,
        /**
         * @deprecated
         */
        endOfFirstCompareMessagesRef,
        /**
         * @deprecated
         */
        endOfSecondCompareMessagesRef,
        editorFloatingTooltipRef,
        floatingElementPluginRef,
        codeEditorRef,
        mainMessageListRef,
        compareMessageListRef,
        imageEditorSwiperRef,
        scrollableConversationsContainerRef,
      }}
    >
      {children}
    </ForwardRefContext.Provider>
  );
};

export { ForwardRefContextProvider };
export default ForwardRefContext;
